import type { Element, ElementType } from 'react';
import React, { useEffect, useRef } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ESCAPE } from 'Utils/keyCodes';
import { themes } from 'Components/frameworks/drop-down/DropDown.themes';
type DropDownProps = {
  children: (handler: () => void, isExpanded: boolean) => Element<any>;
  className?: string;
  contentStyle?: CSSRuleList;
  isExpanded?: boolean;
  renderTrigger?: (handler: () => void, isExpanded: boolean) => Element<any>;
  rootRef?: {
    current: React$ElementRef<ElementType> | null | undefined;
  };
  theme?: keyof typeof themes;
  onClose?: () => void;
  toggleExpansion: () => void;
  hasCloseOnClick?: boolean;
};
export const DropDownSimplified = ({
  children,
  className,
  renderTrigger,
  isExpanded = false,
  toggleExpansion,
  hasCloseOnClick,
  rootRef,
  onClose,
  theme,
}: DropDownProps) => {
  const contentRef = useRef(null);
  const thisRootRef = useRef(null);

  const collapse = () => {
    if (onClose) {
      onClose();
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === ESCAPE) {
      collapse();
    }
  };

  const onClickOutside = (e: MouseEvent | TouchEvent) => {
    if (
      !contentRef ||
      contentRef.current.contains(e.target) ||
      thisRootRef.current.contains(e.target) || // @ts-expect-error TODO(flow-migration)
      e.offsetX > e.target.clientWidth || // @ts-expect-error TODO(flow-migration)
      e.offsetY > e.target.clientHeight ||
      (rootRef?.current && rootRef?.current?.contains(e.target)) ||
      (e.button && e.button !== 0)
    ) {
      return;
    }

    collapse();
  };

  const removeEventListeners = () => {
    if (typeof window !== 'undefined') {
      document.removeEventListener('mouseup', onClickOutside, false);
      document.removeEventListener('touchstart', onClickOutside, false);
      document.removeEventListener('keydown', onKeyDown, false);
    }
  };

  const addEventListeners = () => {
    if (typeof window !== 'undefined') {
      document.addEventListener('mouseup', onClickOutside, false);
      document.addEventListener('touchstart', onClickOutside, false);
      document.addEventListener('keydown', onKeyDown, false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      addEventListeners();
    } else {
      removeEventListeners();
    }

    return () => {
      removeEventListeners();
    };
  }, [isExpanded]);
  const { thBody } = themes({})[theme];
  return (
    <div className={classnames('relative inline-block', className)} ref={thisRootRef}>
      {renderTrigger && renderTrigger(toggleExpansion, isExpanded)}
      {isExpanded && (
        <div
          className={classnames(
            'absolute left-1/2 z-6 w-500 min-w-full -translate-x-1/2 transform',
            thBody,
          )}
          data-testid="DropDown-body-simplified"
          onClick={hasCloseOnClick ? collapse : null}
          onKeyPress={hasCloseOnClick ? collapse : null}
          ref={contentRef}
          role="button"
          tabIndex={0}
        >
          {children(toggleExpansion, isExpanded)}
        </div>
      )}
    </div>
  );
};
